import { formatNumber, formatOrdinal, MEASURE_COLOR_LIST } from "../utils/helper"
import type { SamplesResponseType } from "../../context/samplesContext"
import type { MeasurementReportGroup } from "./types"

export const PREVIEW_MAX_WIDTH = 1024
export const DEFAULT_STROKE_COLOR = "#FF6C6C"

export function extractFileNameFromHeader(header: string | null): string | null {
  if (!header) {
    return null
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match && utf8Match[1]) {
    const decodedCandidate = utf8Match[1]
    try {
      return decodeURIComponent(decodedCandidate)
    } catch {
      return decodedCandidate
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i)
  if (asciiMatch && asciiMatch[1]) {
    return asciiMatch[1]
  }
  return null
}

export async function buildAnnotatedImage(
  sample: SamplesResponseType,
  getColorFn: (originalIdx: number) => string,
  getDisplayIndexFn: (originalIdx: number) => number
): Promise<string | null> {
  const baseImageEntry = sample.sampleImages?.[0]
  if (!baseImageEntry?.url) {
    return null
  }

  const imageElement = await loadImageElement(baseImageEntry.url)
  const desiredMaxWidth = 930
  const aspectRatio = imageElement.width / Math.max(1, imageElement.height)
  const displayWidth = Math.max(1, Math.min(imageElement.width, desiredMaxWidth))
  const displayHeight = Math.max(1, Math.round(displayWidth / aspectRatio))
  const dpr = window.devicePixelRatio || 1

  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.floor(displayWidth * dpr))
  canvas.height = Math.max(1, Math.floor(displayHeight * dpr))

  const ctx = canvas.getContext("2d")
  if (!ctx) {
    return null
  }

  if (typeof ctx.resetTransform === "function") {
    ctx.resetTransform()
  }
  ctx.scale(dpr, dpr)
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"

  ctx.clearRect(0, 0, displayWidth, displayHeight)
  ctx.drawImage(
    imageElement,
    0,
    0,
    imageElement.width,
    imageElement.height,
    0,
    0,
    displayWidth,
    displayHeight
  )

  const scaleX = displayWidth / Math.max(1, imageElement.width)
  const scaleY = displayHeight / Math.max(1, imageElement.height)

  const activeMeasurements = sample.sampleMeasurements
    .filter(measurement => !measurement.is_deleted)
    .sort((a, b) => {
      const idxA = typeof a.measure_index === "number" ? a.measure_index : Number.MAX_SAFE_INTEGER
      const idxB = typeof b.measure_index === "number" ? b.measure_index : Number.MAX_SAFE_INTEGER
      if (idxA === idxB) {
        return (a.measure_name || "").localeCompare(b.measure_name || "")
      }
      return idxA - idxB
    })

  const fallbackColorMap = new Map<number, string>()
  const fallbackDisplayIndexMap = new Map<number, number>()
  activeMeasurements.forEach((measurement, idx) => {
    if (typeof measurement.measure_index !== "number" || measurement.measure_index <= 0) {
      return
    }
    const color = MEASURE_COLOR_LIST[idx]?.color ?? DEFAULT_STROKE_COLOR
    fallbackColorMap.set(measurement.measure_index, color)
    fallbackDisplayIndexMap.set(measurement.measure_index, idx + 1)
  })

  const drawableMeasurements = activeMeasurements
    .filter(measurement => Array.isArray(measurement.result_point) && measurement.result_point.length >= 2)
    .map((measurement, idx) => ({
      measurement,
      displayIndex: idx + 1
    }))

  drawableMeasurements.forEach(({ measurement, displayIndex }) => {
    const points = measurement.result_point.map(point => ({
      x: point.x * scaleX,
      y: point.y * scaleY
    }))

    if (points.length < 2) {
      return
    }

    const baseIndex =
      typeof measurement.measure_index === "number" && measurement.measure_index > 0
        ? measurement.measure_index
        : displayIndex

    const contextDisplayIndex = getDisplayIndexFn?.(baseIndex) ?? 0
    const fallbackDisplayIndex = fallbackDisplayIndexMap.get(baseIndex) ?? displayIndex
    const useContextDisplayIndex =
      contextDisplayIndex > 0 && contextDisplayIndex === fallbackDisplayIndex
    // NOTE: context由来の表示番号は外部状態に依存してズレる可能性があるため、
    // NOTE: 計算済みのfallbackと一致する場合のみ採用する。ズレている場合は
    // NOTE: ラベル番号の不整合や表示順の混乱を防ぐためfallbackを優先する。
    const effectiveDisplayIndex = useContextDisplayIndex ? contextDisplayIndex : fallbackDisplayIndex

    const colorCandidate = getColorFn?.(baseIndex)
    const fallbackColor = fallbackColorMap.get(baseIndex) ?? getStrokeColor(baseIndex)
    // NOTE: context由来の色(colorCandidate)はフィルタリングや並び替えにより
    // NOTE: 外部状態とローカル計算で不整合が生じる可能性がある。そのため、
    // NOTE: fallbackColorと一致する場合のみcontextの色を採用し、不一致の場合は
    // NOTE: fallbackを優先することで描画色の不整合を防止する。
    const useContextColor =
      Boolean(colorCandidate) &&
      colorCandidate !== "black" &&
      colorCandidate === fallbackColor
    const strokeColor = useContextColor ? colorCandidate : fallbackColor

    ctx.strokeStyle = strokeColor
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    points.slice(1).forEach(point => ctx.lineTo(point.x, point.y))
    ctx.stroke()

    const first = points[0]
    const second = points[1]
    const mid = {
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2
    }
    const segPx = Math.hypot(second.x - first.x, second.y - first.y)
    const fontSize = Math.min(20, Math.max(10, segPx * 0.06))
    const radius = fontSize * 0.55

    ctx.beginPath()
    ctx.fillStyle = "#FFFFFF"
    ctx.arc(mid.x, mid.y, radius, 0, Math.PI * 2)
    ctx.fill()

    const labelIndex = effectiveDisplayIndex > 0 ? effectiveDisplayIndex : displayIndex
    const labelText = labelIndex > 0 ? formatNumber(labelIndex) : String(labelIndex)

    ctx.fillStyle = "#000000"
    ctx.font = `${fontSize}px sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(labelText, mid.x, mid.y)
  })

  return canvas.toDataURL("image/png")
}

export function getKindDisplay(item: Pick<MeasurementReportGroup, "has_final" | "representative_kind" | "time_values">) {
  if (item.has_final || item.representative_kind === "final") {
    return "FINAL"
  }
  if (item.time_values.length > 0) {
    return item.time_values.map(value => formatOrdinal(value)).join(", ")
  }
  return "-"
}

function getStrokeColor(measureIndex: number): string {
  const entry = MEASURE_COLOR_LIST.find(item => item.measure_index === measureIndex)
  return entry?.color ?? DEFAULT_STROKE_COLOR
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}
